import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { MdAdd, MdCalendarToday, MdPerson, MdFilterList, MdVisibility, MdPayment } from 'react-icons/md';
import { recibosAPI, clientesAPI, gruposAPI } from '../services/api';
import { SkeletonTableRows, EmptyState } from '../components/SkeletonLoader';

const ReceiptsContainer = styled.div`
  max-width: 1400px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xl};
  gap: ${props => props.theme.spacing.md};
  flex-wrap: wrap;

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    margin-bottom: ${p => p.theme.spacing.md};
  }
`;

const Title = styled.h1`
  font-size: ${props => props.theme.fontSizes.xxxl};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  flex-wrap: wrap;
`;

const Button = styled.button`
  padding: 10px ${props => props.theme.spacing.lg};
  background: ${props => props.primary
    ? `linear-gradient(135deg, ${props.theme.colors.primary}, ${props.theme.colors.primaryDark})`
    : props.theme.colors.backgroundDark};
  color: ${props => props.primary ? 'white' : props.theme.colors.textSecondary};
  border: none;
  border-radius: ${props => props.theme.borderRadius.md};
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: 600;
  font-family: ${props => props.theme.fonts.primary};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  transition: ${props => props.theme.transitions.fast};
  box-shadow: ${props => props.primary ? `0 4px 14px ${props.theme.colors.primary}40` : props.theme.shadows.sm};

  svg { font-size: 1.2rem; }

  &:hover {
    transform: translateY(-1px);
    box-shadow: ${props => props.primary ? `0 6px 20px ${props.theme.colors.primary}50` : props.theme.shadows.md};
  }
`;

const FilterSection = styled.div`
  background: ${props => props.theme.colors.backgroundLight};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.card};
  display: flex;
  gap: ${props => props.theme.spacing.md};
  flex-wrap: wrap;

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    padding: ${p => p.theme.spacing.md};
    margin-bottom: ${p => p.theme.spacing.md};
    border-radius: ${p => p.theme.borderRadius.md};
    gap: ${p => p.theme.spacing.sm};
  }
`;

const FilterButton = styled.button`
  padding: 7px 18px;
  background: ${props => props.active
    ? `linear-gradient(135deg, ${props.theme.colors.primary}, ${props.theme.colors.primaryDark})`
    : props.theme.colors.backgroundDark};
  color: ${props => props.active ? 'white' : props.theme.colors.textSecondary};
  border: none;
  border-radius: 99px;
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.active ? 600 : 500};
  font-family: ${props => props.theme.fonts.primary};
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;
  box-shadow: ${props => props.active ? `0 4px 12px ${props.theme.colors.primary}35` : 'none'};

  svg { font-size: 1rem; }

  &:hover {
    background: ${props => props.active
      ? `linear-gradient(135deg, ${props.theme.colors.primary}, ${props.theme.colors.primaryDark})`
      : props.theme.colors.border};
    color: ${props => props.active ? 'white' : props.theme.colors.textPrimary};
  }
`;

const FilterControls = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  flex-wrap: wrap;
  width: 100%;
  margin-top: ${props => props.theme.spacing.md};
  padding-top: ${props => props.theme.spacing.md};
  border-top: 1px solid ${props => props.theme.colors.border};
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
  flex: 1;
  min-width: 200px;
`;

const FilterLabel = styled.label`
  font-size: ${props => props.theme.fontSizes.xs};
  color: ${props => props.theme.colors.textSecondary};
  font-weight: ${props => props.theme.fontWeights.medium};
`;

const FilterInput = styled.input`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textPrimary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.fontSizes.sm};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const FilterSelect = styled.select`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textPrimary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.fontSizes.sm};
  cursor: pointer;
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;

const ClearFiltersButton = styled.button`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background-color: transparent;
  color: ${props => props.theme.colors.textSecondary};
  border: none;
  font-size: ${props => props.theme.fontSizes.xs};
  text-decoration: underline;
  cursor: pointer;
  align-self: flex-end;
  
  &:hover {
    color: ${props => props.theme.colors.primary};
  }
`;

const ReceiptsListSection = styled.div`
  background: ${props => props.theme.colors.backgroundLight};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.xl};
  box-shadow: ${props => props.theme.shadows.card};

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    padding: ${p => p.theme.spacing.md};
    border-radius: ${p => p.theme.borderRadius.md};
  }
`;

const SectionHeader = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes.xl};
  color: ${props => props.theme.colors.textPrimary};
  margin: 0 0 ${props => props.theme.spacing.xs} 0;
`;

const SectionSubtitle = styled.p`
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textSecondary};
  margin: 0;
`;

const TableWrapper = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    display: none;
  }
`;

/* ── Mobile card list ──────────────────────────────────── */
const MobileCardList = styled.div`
  display: none;

  @media (max-width: ${p => p.theme.breakpoints.tablet}) {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
`;

const ReceiptCard = styled.div`
  background: ${p => p.theme.colors.background};
  border: 1px solid ${p => p.theme.colors.border};
  border-left: 4px solid ${p =>
    p.estado === 'pagado' ? p.theme.colors.success :
    p.estado === 'vencido' ? p.theme.colors.danger :
    p.estado === 'parcial' ? p.theme.colors.info :
    p.theme.colors.warning
  };
  border-radius: ${p => p.theme.borderRadius.md};
  padding: 14px 16px;
  cursor: pointer;
  transition: box-shadow 0.15s ease, transform 0.15s ease;
  -webkit-tap-highlight-color: transparent;

  &:active {
    transform: scale(0.985);
    box-shadow: ${p => p.theme.shadows.sm};
  }
`;

const ReceiptCardTop = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 10px;
`;

const CardAvatar = styled.div`
  width: 38px;
  height: 38px;
  border-radius: 10px;
  background: ${p => p.bg};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.75rem;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
`;

const ReceiptCardName = styled.div`
  flex: 1;
  min-width: 0;
  font-size: ${p => p.theme.fontSizes.sm};
  font-weight: 600;
  color: ${p => p.theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ReceiptCardBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const ReceiptCardMeta = styled.div`
  font-size: ${p => p.theme.fontSizes.xs};
  color: ${p => p.theme.colors.textSecondary};
`;

const ReceiptCardAmount = styled.div`
  font-size: ${p => p.theme.fontSizes.md};
  font-weight: 700;
  color: ${p => p.hasBalance ? p.theme.colors.danger : p.theme.colors.success};
`;

const ReceiptCardActions = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const SmallBtn = styled.button`
  padding: 6px 14px;
  border: none;
  border-radius: 8px;
  font-size: ${p => p.theme.fontSizes.xs};
  font-weight: 600;
  font-family: ${p => p.theme.fonts.primary};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: ${p => p.theme.transitions.fast};
  background: ${p => p.secondary
    ? `linear-gradient(135deg, ${p.theme.colors.success}, #38A169)`
    : `linear-gradient(135deg, ${p.theme.colors.primary}, ${p.theme.colors.primaryDark})`};
  color: white;
  -webkit-tap-highlight-color: transparent;

  svg { font-size: 0.9rem; }
  &:active { opacity: 0.85; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 700px;
`;

const Thead = styled.thead``;

const Th = styled.th`
  padding: 8px ${props => props.theme.spacing.md};
  text-align: left;
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: 700;
  color: ${props => props.theme.colors.textLight};
  text-transform: uppercase;
  letter-spacing: 0.06em;
  border-bottom: 1px solid ${props => props.theme.colors.border};
`;

const Td = styled.td`
  padding: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSizes.sm};
  color: ${props => props.theme.colors.textPrimary};
  border-bottom: 1px solid ${props => props.theme.colors.borderLight};
`;

const ClientAvatarCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const ClientAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  background: ${p => p.bg || 'linear-gradient(135deg,#4FD1C5,#38B2AC)'};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.7rem;
  font-weight: 700;
  color: white;
  flex-shrink: 0;
`;

const SaldoCell = styled.span`
  font-weight: 600;
  color: ${p => p.hasBalance ? p.theme.colors.danger : p.theme.colors.success};
`;

const PaymentMethod = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.fontSizes.sm};
`;

const MethodIcon = styled.span`
  font-size: ${props => props.theme.fontSizes.lg};
`;

const StatusBadge = styled.span`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: ${props => props.theme.fontWeights.medium};
  background-color: ${props => {
    const status = props.status?.toLowerCase();
    if (status === 'pagado') return props.theme.colors.success + '20';
    if (status === 'vencido') return props.theme.colors.danger + '20';
    if (status === 'parcial') return props.theme.colors.info + '20';
    if (status === 'cancelado') return props.theme.colors.textSecondary + '20';
    if (status === 'reembolsado') return '#FF5722' + '20';
    return props.theme.colors.warning + '20';
  }};
  color: ${props => {
    const status = props.status?.toLowerCase();
    if (status === 'pagado') return props.theme.colors.success;
    if (status === 'vencido') return props.theme.colors.danger;
    if (status === 'parcial') return props.theme.colors.info;
    if (status === 'cancelado') return props.theme.colors.textSecondary;
    if (status === 'reembolsado') return '#FF5722';
    return props.theme.colors.warning;
  }};
  text-decoration: ${props => {
    const status = props.status?.toLowerCase();
    if (status === 'cancelado' || status === 'reembolsado') return 'line-through';
    return 'none';
  }};
`;

const ActionsCell = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.xs};
  align-items: center;
`;

const ActionButton = styled.button`
  padding: 5px 14px;
  background: ${props => props.secondary
    ? `linear-gradient(135deg, ${props.theme.colors.success}, #38A169)`
    : `linear-gradient(135deg, ${props.theme.colors.primary}, ${props.theme.colors.primaryDark})`};
  color: white;
  border: none;
  border-radius: 8px;
  font-size: ${props => props.theme.fontSizes.xs};
  font-weight: 600;
  font-family: ${props => props.theme.fonts.primary};
  display: inline-flex;
  align-items: center;
  gap: 4px;
  transition: ${props => props.theme.transitions.fast};

  svg { font-size: 1rem; }

  &:hover { transform: translateY(-1px); box-shadow: 0 4px 10px rgba(0,0,0,0.15); }
  &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
`;

const Pagination = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.xl};
  flex-wrap: wrap;
`;

const PageButton = styled.button`
  padding: 6px 14px;
  background: ${props => props.active
    ? `linear-gradient(135deg, ${props.theme.colors.primary}, ${props.theme.colors.primaryDark})`
    : props.theme.colors.backgroundDark};
  color: ${props => props.active ? 'white' : props.theme.colors.textSecondary};
  border: none;
  border-radius: 8px;
  font-size: ${props => props.theme.fontSizes.sm};
  font-weight: ${props => props.active ? 600 : 400};
  font-family: ${props => props.theme.fonts.primary};
  transition: ${props => props.theme.transitions.fast};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: ${props => props.active
      ? `linear-gradient(135deg, ${props.theme.colors.primary}, ${props.theme.colors.primaryDark})`
      : props.theme.colors.border};
  }

  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const getAvatarGradient = (nombre) => {
  const gradients = [
    'linear-gradient(135deg,#4FD1C5,#38B2AC)',
    'linear-gradient(135deg,#667eea,#764ba2)',
    'linear-gradient(135deg,#f093fb,#f5576c)',
    'linear-gradient(135deg,#4facfe,#00f2fe)',
    'linear-gradient(135deg,#43e97b,#38f9d7)',
    'linear-gradient(135deg,#fa709a,#fee140)',
  ];
  return gradients[(nombre?.charCodeAt(0) || 0) % gradients.length];
};

const getInitials = (nombre) =>
  (nombre || '?').split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase();

const Receipts = () => {
  const navigate = useNavigate();
  const [recibos, setRecibos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const recibosPerPage = 10;
  
  // Estados de filtros
  const [filtroEstado, setFiltroEstado] = useState('todos');
  const [filtroCliente, setFiltroCliente] = useState('');
  const [filtroGrupo, setFiltroGrupo] = useState('');
  const [filtroFechaInicio, setFiltroFechaInicio] = useState('');
  const [filtroFechaFin, setFiltroFechaFin] = useState('');
  const [showFiltros, setShowFiltros] = useState(false);

  const [clientesInfo, setClientesInfo] = useState({}); // cliente_id -> { grupo_id, grupo_nombre, padre_cliente_id, padre_nombre }
  const [grupos, setGrupos] = useState([]);

  useEffect(() => {
    loadRecibos();
    clientesAPI.getAll().then(res => {
      const map = {};
      (res.data.data || []).forEach(c => {
        map[c.id] = { grupo_id: c.grupo_id, grupo_nombre: c.grupo_nombre, padre_cliente_id: c.padre_cliente_id, padre_nombre: c.padre_nombre };
      });
      setClientesInfo(map);
    }).catch(() => {});
    gruposAPI.getAll().then(res => setGrupos(res.data.data || [])).catch(() => {});
  }, []);

  // Resetear a página 1 cuando cambian los filtros
  useEffect(() => {
    setCurrentPage(1);
  }, [filtroEstado, filtroCliente, filtroGrupo, filtroFechaInicio, filtroFechaFin]);

  const loadRecibos = async () => {
    try {
      setLoading(true);
      const response = await recibosAPI.getAll();
      const recibosData = response.data.data || [];
      
      // Debug: Ver qué estados vienen de la BD
      console.log('Estados de recibos:', recibosData.map(r => ({ 
        id: r.id, 
        numero: r.numero_recibo,
        estado: r.estado 
      })));
      
      setRecibos(recibosData);
      setError(null);
    } catch (err) {
      setError(err.message || 'Error al cargar recibos');
      console.error('Error al cargar recibos:', err);
    } finally {
      setLoading(false);
    }
  };
  
  const formatEstado = (estado) => {
    if (!estado) return '';
    return estado.charAt(0).toUpperCase() + estado.slice(1).toLowerCase();
  };
  
  const getPaymentIcon = (method) => {
    if (method && method.includes('Visa')) return '💳';
    if (method && method.includes('Mastercard')) return '💳';
    if (method && method.includes('PayPal')) return '🏦';
    if (method && method.includes('Transferencia')) return '🏦';
    return '💰';
  };

  // Aplicar filtros
  const recibosFiltrados = recibos.filter(recibo => {
    // Filtro por estado (case-insensitive)
    if (filtroEstado !== 'todos') {
      const estadoRecibo = (recibo.estado || '').toLowerCase();
      const estadoFiltro = filtroEstado.toLowerCase();
      
      // Debug
      if (recibo.id === recibos[0]?.id) {
        console.log('Filtro estado debug:', {
          filtroEstado,
          estadoFiltro,
          'recibo.estado': recibo.estado,
          estadoRecibo
        });
      }
      
      if (estadoFiltro === 'pendiente' && estadoRecibo !== 'pendiente') return false;
      if (estadoFiltro === 'pagado' && estadoRecibo !== 'pagado') return false;
      if (estadoFiltro === 'vencido' && estadoRecibo !== 'vencido') return false;
      if (estadoFiltro === 'parcial' && estadoRecibo !== 'parcial') return false;
    }
    
    // Filtro por cliente
    if (filtroCliente && !recibo.cliente_nombre.toLowerCase().includes(filtroCliente.toLowerCase())) {
      return false;
    }

    // Filtro por grupo de clientes
    if (filtroGrupo && String(clientesInfo[recibo.cliente_id]?.grupo_id || '') !== filtroGrupo) {
      return false;
    }

    // Filtro por fecha
    if (filtroFechaInicio) {
      const fechaRecibo = new Date(recibo.fecha_emision);
      const fechaInicio = new Date(filtroFechaInicio);
      if (fechaRecibo < fechaInicio) return false;
    }
    
    if (filtroFechaFin) {
      const fechaRecibo = new Date(recibo.fecha_emision);
      const fechaFin = new Date(filtroFechaFin);
      if (fechaRecibo > fechaFin) return false;
    }
    
    return true;
  });

  const limpiarFiltros = () => {
    setFiltroEstado('todos');
    setFiltroCliente('');
    setFiltroGrupo('');
    setFiltroFechaInicio('');
    setFiltroFechaFin('');
    setCurrentPage(1);
  };

  // Calcular paginación sobre recibos filtrados
  const indexOfLastRecibo = currentPage * recibosPerPage;
  const indexOfFirstRecibo = indexOfLastRecibo - recibosPerPage;
  const currentRecibos = recibosFiltrados.slice(indexOfFirstRecibo, indexOfLastRecibo);
  const totalPages = Math.ceil(recibosFiltrados.length / recibosPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  // Generar números de página para mostrar
  const getPageNumbers = () => {
    const pages = [];
    const maxPagesToShow = 5;
    
    if (totalPages <= maxPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) {
          pages.push(i);
        }
        pages.push('...');
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        pages.push(1);
        pages.push('...');
        pages.push(currentPage - 1);
        pages.push(currentPage);
        pages.push(currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    
    return pages;
  };

  return (
    <ReceiptsContainer>
      <PageHeader>
        <Title>Recibos y Facturas</Title>
        <ActionButtons>
          <Button primary onClick={() => navigate('/registrar-pago')}>
            <MdAdd />
            Registrar Pago
          </Button>
          <Button onClick={() => navigate('/recibos/nuevo')}>
            <MdAdd />
            Nuevo Recibo
          </Button>
        </ActionButtons>
      </PageHeader>

      <FilterSection>
        <FilterButton 
          active={showFiltros}
          onClick={() => setShowFiltros(!showFiltros)}
        >
          <MdFilterList />
          {showFiltros ? 'Ocultar Filtros' : 'Mostrar Filtros'}
        </FilterButton>
        
        {showFiltros && (
          <FilterControls>
            <FilterGroup>
              <FilterLabel>Estado</FilterLabel>
              <FilterSelect 
                value={filtroEstado} 
                onChange={(e) => {
                  setFiltroEstado(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="todos">Todos</option>
                <option value="pendiente">Pendiente</option>
                <option value="pagado">Pagado</option>
                <option value="parcial">Parcial</option>
                <option value="vencido">Vencido</option>
              </FilterSelect>
            </FilterGroup>
            
            <FilterGroup>
              <FilterLabel>Cliente</FilterLabel>
              <FilterInput 
                type="text"
                placeholder="Buscar por nombre..."
                value={filtroCliente}
                onChange={(e) => {
                  setFiltroCliente(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </FilterGroup>
            
            <FilterGroup>
              <FilterLabel>Grupo de Clientes</FilterLabel>
              <FilterSelect
                value={filtroGrupo}
                onChange={(e) => {
                  setFiltroGrupo(e.target.value);
                  setCurrentPage(1);
                }}
              >
                <option value="">Todos</option>
                {grupos.map(g => (
                  <option key={g.id} value={String(g.id)}>{g.nombre}</option>
                ))}
              </FilterSelect>
            </FilterGroup>

            <FilterGroup>
              <FilterLabel>Fecha Inicio</FilterLabel>
              <FilterInput 
                type="date"
                value={filtroFechaInicio}
                onChange={(e) => {
                  setFiltroFechaInicio(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </FilterGroup>
            
            <FilterGroup>
              <FilterLabel>Fecha Fin</FilterLabel>
              <FilterInput 
                type="date"
                value={filtroFechaFin}
                onChange={(e) => {
                  setFiltroFechaFin(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </FilterGroup>
            
            <ClearFiltersButton onClick={limpiarFiltros}>
              Limpiar filtros
            </ClearFiltersButton>
          </FilterControls>
        )}
      </FilterSection>

      <ReceiptsListSection>
        <SectionHeader>
          <SectionTitle>Lista de Recibos</SectionTitle>
          <SectionSubtitle>
            {recibosFiltrados.length} recibo{recibosFiltrados.length !== 1 ? 's' : ''} 
            {recibosFiltrados.length !== recibos.length && ` (filtrados de ${recibos.length} totales)`}
          </SectionSubtitle>
        </SectionHeader>
        
        {/* Desktop: tabla */}
        <TableWrapper>
        <Table>
          <Thead>
            <tr>
              <Th>Cliente</Th>
              <Th>Fecha</Th>
              <Th>Monto Total</Th>
              <Th>Monto Pendiente</Th>
              <Th>No. de Recibo</Th>
              <Th>Estatus</Th>
              <Th>Acciones</Th>
            </tr>
          </Thead>
          <tbody>
            {loading ? (
              <SkeletonTableRows rows={8} cols={7} />
            ) : currentRecibos.length > 0 ? (
              currentRecibos.map(recibo => (
                <tr key={recibo.id}>
                  <Td>
                    <ClientAvatarCell>
                      <ClientAvatar bg={getAvatarGradient(recibo.cliente_nombre)}>
                        {getInitials(recibo.cliente_nombre)}
                      </ClientAvatar>
                      <div>
                        <div>{recibo.cliente_nombre}</div>
                        {clientesInfo[recibo.cliente_id]?.grupo_nombre && (
                          <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '2px' }}>
                            {clientesInfo[recibo.cliente_id].grupo_nombre}
                          </div>
                        )}
                        {clientesInfo[recibo.cliente_id]?.padre_cliente_id && (
                          <div style={{ fontSize: '0.7rem', color: '#9ca3af', marginTop: '2px' }}>
                            sub-cliente de {clientesInfo[recibo.cliente_id].padre_nombre}
                          </div>
                        )}
                      </div>
                    </ClientAvatarCell>
                  </Td>
                  <Td>{new Date(recibo.fecha_emision).toLocaleDateString('es-MX')}</Td>
                  <Td>${(recibo.monto_total || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</Td>
                  <Td>
                    <SaldoCell hasBalance={parseFloat(recibo.saldo_pendiente || 0) > 0}>
                      ${(recibo.saldo_pendiente || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                    </SaldoCell>
                  </Td>
                  <Td>
                    <PaymentMethod>
                      <MethodIcon>📄</MethodIcon>
                      Recibo #{recibo.numero_recibo}
                    </PaymentMethod>
                  </Td>
                  <Td>
                    <StatusBadge status={formatEstado(recibo.estado)}>{formatEstado(recibo.estado)}</StatusBadge>
                  </Td>
                  <Td>
                    <ActionsCell>
                      <ActionButton onClick={() => navigate(`/recibos/${recibo.id}`)}>
                        <MdVisibility />
                        Ver
                      </ActionButton>
                      {recibo.estado !== 'pagado' && recibo.estado !== 'cancelado' && parseFloat(recibo.saldo_pendiente || 0) > 0 && (
                        <ActionButton
                          secondary
                          onClick={() => navigate('/registrar-pago', { state: { reciboId: recibo.id } })}
                          title="Registrar pago"
                        >
                          <MdPayment />
                          Pagar
                        </ActionButton>
                      )}
                    </ActionsCell>
                  </Td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7">
                  <EmptyState type="receipts" title="Sin recibos" subtitle="No se encontraron recibos con los filtros aplicados" />
                </td>
              </tr>
            )}
          </tbody>
        </Table>
        </TableWrapper>

        {/* Mobile: tarjetas */}
        <MobileCardList>
          {loading ? (
            <div style={{ padding: '2rem', textAlign: 'center' }}>Cargando...</div>
          ) : currentRecibos.length > 0 ? (
            currentRecibos.map(recibo => {
              const saldo = parseFloat(recibo.saldo_pendiente || 0);
              const estado = (recibo.estado || '').toLowerCase();
              const canPay = estado !== 'pagado' && estado !== 'cancelado' && saldo > 0;
              return (
                <ReceiptCard
                  key={recibo.id}
                  estado={estado}
                  onClick={() => navigate(`/recibos/${recibo.id}`)}
                >
                  <ReceiptCardTop>
                    <CardAvatar bg={getAvatarGradient(recibo.cliente_nombre)}>
                      {getInitials(recibo.cliente_nombre)}
                    </CardAvatar>
                    <ReceiptCardName>
                      {recibo.cliente_nombre}
                      {clientesInfo[recibo.cliente_id]?.grupo_nombre && (
                        <div style={{ fontSize: '0.7rem', color: '#9ca3af', fontWeight: 400 }}>
                          {clientesInfo[recibo.cliente_id].grupo_nombre}
                        </div>
                      )}
                      {clientesInfo[recibo.cliente_id]?.padre_cliente_id && (
                        <div style={{ fontSize: '0.7rem', color: '#9ca3af', fontWeight: 400 }}>
                          sub-cliente de {clientesInfo[recibo.cliente_id].padre_nombre}
                        </div>
                      )}
                    </ReceiptCardName>
                    <StatusBadge status={formatEstado(recibo.estado)}>
                      {formatEstado(recibo.estado)}
                    </StatusBadge>
                  </ReceiptCardTop>
                  <ReceiptCardBottom>
                    <ReceiptCardMeta>
                      #{recibo.numero_recibo} · {new Date(recibo.fecha_emision).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </ReceiptCardMeta>
                    <ReceiptCardActions onClick={e => e.stopPropagation()}>
                      <ReceiptCardAmount hasBalance={saldo > 0}>
                        ${saldo.toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                      </ReceiptCardAmount>
                      {canPay && (
                        <SmallBtn
                          secondary
                          onClick={e => { e.stopPropagation(); navigate('/registrar-pago', { state: { reciboId: recibo.id } }); }}
                        >
                          <MdPayment />
                          Pagar
                        </SmallBtn>
                      )}
                    </ReceiptCardActions>
                  </ReceiptCardBottom>
                </ReceiptCard>
              );
            })
          ) : (
            <EmptyState type="receipts" title="Sin recibos" subtitle="No se encontraron recibos con los filtros aplicados" />
          )}
        </MobileCardList>

        {totalPages > 1 && (
          <Pagination>
            <PageButton 
              onClick={handlePrevious} 
              disabled={currentPage === 1}
            >
              Previous
            </PageButton>
            
            {getPageNumbers().map((page, index) => (
              page === '...' ? (
                <span key={`ellipsis-${index}`} style={{ padding: '0 8px' }}>...</span>
              ) : (
                <PageButton
                  key={page}
                  active={currentPage === page}
                  onClick={() => handlePageChange(page)}
                >
                  {page}
                </PageButton>
              )
            ))}
            
            <PageButton 
              onClick={handleNext} 
              disabled={currentPage === totalPages}
            >
              Next
            </PageButton>
          </Pagination>
        )}
      </ReceiptsListSection>
    </ReceiptsContainer>
  );
};

export default Receipts;
